import React, { useMemo, useState } from 'react';
import { X, Mail, MessageCircle, Send, Loader2, ExternalLink, Check } from 'lucide-react';
import { toast } from 'sonner';
import type { Order } from '../../types';
import { adminFollowUp, FollowUpResult, WaTarget } from '../../features/orders/api/orders';

type Channel = 'email' | 'whatsapp' | 'both';

const PRESETS: { key: string; label: string; subject: string; message: string }[] = [
  {
    key: 'reminder',
    label: 'Pengingat Pembayaran',
    subject: 'Selesaikan Pembayaran Pesanan Anda',
    message: 'Halo {name}, pesanan {product} ({order_code}) senilai {amount} masih menunggu pembayaran. Yuk selesaikan sekarang supaya akses Anda langsung aktif. Ada kendala? Balas pesan ini ya 🙏',
  },
  {
    key: 'thanks',
    label: 'Terima Kasih (Lunas)',
    subject: 'Terima kasih atas pembelian Anda',
    message: 'Halo {name}, terima kasih! Pembayaran untuk {product} ({order_code}) sudah kami terima. Selamat belajar & semoga profit konsisten 🚀',
  },
  {
    key: 'info',
    label: 'Info / Konfirmasi Pesanan',
    subject: 'Info Pesanan {order_code}',
    message: 'Halo {name}, kami ingin mengonfirmasi pesanan {product} ({order_code}) dengan status saat ini: {status}. Jika ada pertanyaan, silakan balas pesan ini.',
  },
  { key: 'custom', label: 'Kustom (tulis sendiri)', subject: '', message: '' },
];

const VARS = ['{name}', '{order_code}', '{product}', '{amount}', '{status}'];
const inputCls = 'w-full bg-[#050914] border border-white/10 rounded-lg px-3 py-2 text-white placeholder:text-white/30 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/50 outline-none transition-colors text-sm';

export function FollowUpModal({ orders, onClose }: { orders: Order[]; onClose: () => void }) {
  const [channel, setChannel] = useState<Channel>('both');
  const [preset, setPreset] = useState('reminder');
  const [subject, setSubject] = useState(PRESETS[0].subject);
  const [message, setMessage] = useState(PRESETS[0].message);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<FollowUpResult | null>(null);

  const withEmail = useMemo(() => orders.filter((o) => o.member?.email).length, [orders]);
  const withPhone = useMemo(() => orders.filter((o) => o.member?.phone).length, [orders]);

  const applyPreset = (key: string) => {
    setPreset(key);
    const p = PRESETS.find((x) => x.key === key)!;
    if (key !== 'custom') { setSubject(p.subject); setMessage(p.message); }
  };

  const insertVar = (v: string) => setMessage((m) => `${m}${v}`);

  const send = async () => {
    if (!message.trim()) return toast.error('Pesan wajib diisi');
    setSending(true);
    try {
      const res = await adminFollowUp({ order_ids: orders.map((o) => o.id), channel, subject, message });
      if (channel !== 'whatsapp') {
        toast.success(`Email terkirim: ${res.sent_email}`, { description: res.skipped_no_email ? `${res.skipped_no_email} dilewati (tanpa email)` : undefined });
      }
      if (channel !== 'email') {
        if (res.wa_targets.length === 1) {
          window.open(res.wa_targets[0].wa_link, '_blank', 'noopener,noreferrer');
        }
        setResult(res);
        if (channel === 'whatsapp' && res.wa_targets.length === 0) toast.error('Tidak ada nomor WhatsApp yang valid');
      } else {
        onClose();
      }
    } catch (e: any) {
      toast.error('Gagal', { description: e.response?.data?.detail || e.message });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="followup-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-[#0a1122] border border-white/10 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-cyan-400">Follow Up</p>
            <h3 className="text-lg font-medium text-white">{orders.length} pesanan dipilih</h3>
          </div>
          <button onClick={onClose} data-testid="followup-close" className="text-white/50 hover:text-white p-1"><X className="w-5 h-5" strokeWidth={1.5} /></button>
        </div>

        {result ? (
          <div data-testid="followup-wa-result">
            <p className="text-sm text-white/70 mb-3">Klik tombol di bawah untuk membuka WhatsApp dengan pesan siap kirim ke masing-masing client.</p>
            {result.sent_email > 0 && <p className="text-xs text-emerald-300 mb-3">✓ {result.sent_email} email terkirim</p>}
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {result.wa_targets.map((t: WaTarget) => (
                <a key={t.order_id} href={t.wa_link} target="_blank" rel="noopener noreferrer" data-testid={`followup-wa-open-${t.order_id}`}
                  className="flex items-center gap-3 bg-white/[0.03] border border-emerald-400/20 rounded-lg p-3 hover:bg-emerald-400/10 transition-colors">
                  <MessageCircle className="w-4 h-4 text-emerald-300" strokeWidth={1.5} />
                  <div className="flex-1 min-w-0"><p className="text-sm text-white truncate">{t.name || 'Member'}</p><p className="text-xs text-white/40">{t.phone}</p></div>
                  <ExternalLink className="w-4 h-4 text-emerald-300" strokeWidth={1.5} />
                </a>
              ))}
              {result.wa_targets.length === 0 && <p className="text-white/40 text-sm">Tidak ada nomor WhatsApp valid pada pesanan terpilih.</p>}
            </div>
            {result.skipped_no_phone > 0 && <p className="text-xs text-white/40 mt-3">{result.skipped_no_phone} pesanan dilewati (tanpa nomor).</p>}
            <button onClick={onClose} className="mt-4 w-full bg-white/5 border border-white/10 text-white/80 font-medium px-6 py-2.5 rounded-lg hover:bg-white/10 flex items-center justify-center gap-2"><Check className="w-4 h-4" strokeWidth={2} /> Selesai</button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <p className="block text-xs uppercase tracking-wider text-white/50 mb-1.5">Kirim Via</p>
              <div className="grid grid-cols-3 gap-2">
                {([['both', 'Keduanya'], ['email', 'Email'], ['whatsapp', 'WhatsApp']] as [Channel, string][]).map(([c, l]) => (
                  <button key={c} data-testid={`followup-channel-${c}`} onClick={() => setChannel(c)}
                    className={`flex items-center justify-center gap-1.5 text-sm px-3 py-2 rounded-lg border transition-colors ${channel === c ? 'bg-cyan-400 text-[#050914] border-cyan-400 font-medium' : 'border-white/15 text-white/70 hover:bg-white/5'}`}>
                    {c === 'email' ? <Mail className="w-4 h-4" strokeWidth={1.5} /> : c === 'whatsapp' ? <MessageCircle className="w-4 h-4" strokeWidth={1.5} /> : <Send className="w-4 h-4" strokeWidth={1.5} />} {l}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-white/40 mt-1.5">{withEmail} punya email · {withPhone} punya nomor WA. WhatsApp dibuka via link (kirim manual, aman).</p>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wider text-white/50 mb-1.5">Template</label>
              <select data-testid="followup-preset" className={inputCls} value={preset} onChange={(e) => applyPreset(e.target.value)}>
                {PRESETS.map((p) => <option key={p.key} value={p.key} className="bg-[#050914]">{p.label}</option>)}
              </select>
            </div>

            {channel !== 'whatsapp' && (
              <div>
                <label className="block text-xs uppercase tracking-wider text-white/50 mb-1.5">Subjek Email</label>
                <input data-testid="followup-subject" className={inputCls} value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subjek email..." />
              </div>
            )}

            <div>
              <label className="block text-xs uppercase tracking-wider text-white/50 mb-1.5">Pesan</label>
              <textarea data-testid="followup-message" className={inputCls} rows={5} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Tulis pesan follow-up..." />
              <div className="flex flex-wrap gap-1.5 mt-2">
                {VARS.map((v) => (
                  <button key={v} type="button" onClick={() => insertVar(v)} className="text-[11px] px-2 py-1 rounded-md border border-cyan-400/30 text-cyan-300 hover:bg-cyan-400/10 transition-colors">{v}</button>
                ))}
              </div>
              <p className="text-[11px] text-white/40 mt-1.5">Variabel akan otomatis diisi per client (nama, kode order, produk, jumlah, status).</p>
            </div>

            <button data-testid="followup-send" onClick={send} disabled={sending}
              className="w-full bg-cyan-400 text-[#050914] font-medium px-6 py-2.5 rounded-lg hover:bg-cyan-300 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} /> : <Send className="w-4 h-4" strokeWidth={2} />} Kirim Follow Up
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
